import { useEffect, useRef, useState } from "react";
import { Send, LoaderCircle, Mic, Square, Volume2, VolumeX } from "lucide-react";
import { Reveal } from "../landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POWERS = [
  { emoji: "🎓", label: "Career Guide", prompt: "I have a placement interview next week. Quick tips?" },
  { emoji: "🍔", label: "Food Advisor", prompt: "What's good for dinner under ₹100?" },
  { emoji: "🛍", label: "Shopping Assistant", prompt: "Best budget headphones for studying?" },
  { emoji: "✈️", label: "Travel Planner", prompt: "Plan a Manali weekend under ₹4,000." },
  { emoji: "📚", label: "Learning Coach", prompt: "How do I start learning AI from zero?" },
];

const getSessionId = () => {
  let sid = localStorage.getItem("bitebot_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("bitebot_sid", sid);
  }
  return sid;
};

export const ChatWidget = () => {
  const [messages, setMessages] = useState([
    { role: "bot", text: "Yo! I'm FUNZI 🤖 Ask me about food, jobs, trips, courses — or tap the mic and just say it." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const listRef = useRef(null);
  const busyRef = useRef(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const voiceOnRef = useRef(false);
  voiceOnRef.current = voiceOn;

  const speak = async (text) => {
    try {
      audioRef.current?.pause();
      const res = await fetch(`${API}/bitebot/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.replace(/[*#_]/g, "") }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.play().catch(() => {});
    } catch {
      /* voice is best-effort */
    }
  };

  const send = async (text) => {
    const message = (text ?? "").trim();
    if (!message || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: message }, { role: "bot", text: "" }]);
    try {
      const res = await fetch(`${API}/bitebot/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message, language: localStorage.getItem("funz_lang") || "en" }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "bot", text: copy[copy.length - 1].text + chunk };
          return copy;
        });
      }
      if (voiceOnRef.current) {
        setMessages((m) => {
          speak(m[m.length - 1].text);
          return m;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "bot", text: "Signal dropped. Try that once more?" };
        return copy;
      });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const sendRef = useRef(send);
  sendRef.current = send;

  useEffect(() => {
    const handler = (e) => sendRef.current(e.detail);
    window.addEventListener("bitebot:send", handler);
    return () => window.removeEventListener("bitebot:send", handler);
  }, []);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const toggleRecording = async () => {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 500) return;
        setBusy(true);
        busyRef.current = true;
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const res = await fetch(`${API}/bitebot/stt`, { method: "POST", body: fd });
          const data = await res.json();
          if (data.text?.trim()) {
            setInput("");
            await sendRef.current(data.text.trim());
          }
        } catch {
          /* transcription best-effort */
        } finally {
          busyRef.current = false;
          setBusy(false);
        }
      };
      rec.start();
      setRecording(true);
    } catch {
      setInput("");
    }
  };

  return (
    <div
      data-testid="bitebot-chat"
      className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl sm:p-6"
    >
      <div className="mb-4 flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#06B6D4] text-lg">
          🤖
        </span>
        <div>
          <p className="text-sm font-bold text-white">FUNZI</p>
          <p className="flex items-center gap-1.5 font-jbmono text-[10px] uppercase tracking-widest text-[#06B6D4]">
            <span className="animate-pulse-dot h-1.5 w-1.5 rounded-full bg-[#06B6D4]" />
            Live AI · Voice ready
          </p>
        </div>
        <button
          data-testid="bitebot-voice-toggle"
          onClick={() => {
            setVoiceOn((v) => !v);
            if (voiceOn) audioRef.current?.pause();
          }}
          aria-label="Toggle spoken replies"
          className={`ml-auto flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
            voiceOn
              ? "border-[#06B6D4]/60 bg-[#06B6D4]/15 text-[#06B6D4]"
              : "border-white/10 bg-white/5 text-slate-400 hover:text-white"
          }`}
        >
          {voiceOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </button>
      </div>

      <div ref={listRef} className="no-scrollbar h-64 space-y-3 overflow-y-auto pr-1 sm:h-72" data-testid="bitebot-messages">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
              m.role === "user"
                ? "ml-auto rounded-tr-md border border-[#7C3AED]/30 bg-[#7C3AED]/20 text-white"
                : "rounded-tl-md border border-white/10 bg-[#09090B]/80 text-slate-200"
            }`}
          >
            {m.text || (busy && i === messages.length - 1 ? <span className="animate-pulse">…</span> : "")}
          </div>
        ))}
      </div>

      <form
        className="mt-4 flex items-center gap-2 rounded-full border border-white/10 bg-[#09090B]/80 py-1.5 pl-5 pr-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
          setInput("");
        }}
      >
        <input
          data-testid="bitebot-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={recording ? "Listening… tap to stop" : "Ask FUNZI anything…"}
          className="h-10 flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 outline-none"
        />
        <button
          data-testid="bitebot-mic"
          type="button"
          onClick={toggleRecording}
          aria-label="Talk to FUNZI"
          className={`flex h-10 w-10 items-center justify-center rounded-full border transition-all ${
            recording
              ? "animate-pulse border-[#F97316] bg-[#F97316]/20 text-[#F97316]"
              : "border-white/10 bg-white/5 text-slate-300 hover:border-[#7C3AED]/60 hover:text-white"
          }`}
        >
          {recording ? <Square className="h-3.5 w-3.5 fill-current" /> : <Mic className="h-4 w-4" />}
        </button>
        <button
          data-testid="bitebot-send"
          type="submit"
          disabled={busy}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white transition-transform hover:scale-105 disabled:opacity-50"
        >
          {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
};

export const FUNZI = () => (
  <section id="bitebot" data-testid="bitebot-section" className="relative overflow-hidden py-20 sm:py-24">
    <div aria-hidden="true" className="absolute left-1/2 top-1/2 h-[440px] w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#7C3AED]/15 blur-[140px]" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="relative mb-10 w-fit" data-testid="bitebot-mascot">
            <div aria-hidden="true" className="absolute inset-0 -m-5 animate-[spin_14s_linear_infinite] rounded-full border-2 border-dashed border-[#06B6D4]/30" />
            <div className="animate-floaty relative flex h-32 w-32 items-center justify-center rounded-[2rem] bg-gradient-to-br from-[#7C3AED] to-[#06B6D4] text-6xl shadow-[0_30px_90px_-20px_rgba(108,99,255,0.6)] sm:h-36 sm:w-36">
              🤖
            </div>
          </div>
          <p className="font-jbmono text-xs uppercase tracking-[0.3em] text-[#06B6D4]">🤖 Meet FUNZI</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Not a chatbot. Your campus bestie.
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-slate-400">
            FUNZI is live — a real AI that knows food, jobs, trips, courses and your
            budget. Type, or tap the mic and talk. Flip the speaker on and it talks back.
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {POWERS.map((p) => (
              <button
                key={p.label}
                data-testid={`bitebot-power-${p.label.toLowerCase().replace(" ", "-")}`}
                onClick={() => window.dispatchEvent(new CustomEvent("bitebot:send", { detail: p.prompt }))}
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-slate-200 backdrop-blur-xl transition-colors hover:border-[#06B6D4]/50"
              >
                <span>{p.emoji}</span>
                {p.label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <ChatWidget />
        </Reveal>
      </div>
    </div>
  </section>
);
